import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Topbar from '../component/Topbar';
import { BiSearchAlt, BiDownload, BiUpload, BiIdCard, BiRefresh } from 'react-icons/bi';
import { PiMicrosoftExcelLogoDuotone } from 'react-icons/pi';
import ReactPaginate from 'react-paginate';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import LoadingScreen from '../component/LoadingScreen';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { acquireGetAccessToken } from '../utils/GetGrapApi';
import { checkRole, UserRole } from '../callMsGroup';
import { saveRealRole, getEffectiveRole } from '../utils/roleOverride';
import { Buffer } from 'buffer';

interface EmployeeCardRow {
    employeeId: string;
    cardNo: string | null;
    displayName: string | null;
    department: string | null;
    jobTitle: string | null;
    mail: string | null;
}

interface EmployeeCardPage {
    total: number;
    totalPages: number;
    offset: number;
    limit: number;
    items: EmployeeCardRow[];
}

interface ImportResult {
    updated: number;
    skipped: number;
    errors: string[];
}

interface SyncResult {
    added: number;
    updated: number;
    total: number;
    error?: string;
}

const PAGE_SIZE = 20;

function UpdateCardNo() {
    const { inProgress, instance, accounts } = useMsal();
    const [token, setToken] = useState('');
    const [photoData, setPhotoData] = useState('');
    const [role, setRole] = useState<UserRole>('owner');
    const [stateLoad, setStateLoad] = useState(false);

    const [data, setData] = useState<EmployeeCardPage>({ total: 0, totalPages: 0, offset: 0, limit: PAGE_SIZE, items: [] });
    const [keyword, setKeyword] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);

    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    const importRef = useRef<HTMLInputElement>(null);

    const authHeader = () => ({ Authorization: 'Bearer ' + sessionStorage.getItem('accessToken') });

    const loadData = async (kw: string, page: number) => {
        setLoading(true);
        try {
            const res = await axios.get<EmployeeCardPage>(
                `${process.env.REACT_APP_API_END_POINT}/v1/EmployeeCard?keyword=${encodeURIComponent(kw)}&offset=${page * PAGE_SIZE}&limit=${PAGE_SIZE}`,
                { headers: authHeader() }
            );
            setData(res.data);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setCurrentPage(0);
        loadData(keyword, 0);
    };

    const handleClear = () => {
        setKeyword('');
        setCurrentPage(0);
        loadData('', 0);
    };

    const handlePageChange = (selected: number) => {
        setCurrentPage(selected);
        loadData(keyword, selected);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/EmployeeCard/export?keyword=${encodeURIComponent(keyword)}`,
                { headers: authHeader(), responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Employees_CardNo_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setImporting(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const res = await axios.post<ImportResult>(
                `${process.env.REACT_APP_API_END_POINT}/v1/EmployeeCard/import`,
                form,
                { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } }
            );
            setImportResult(res.data);
            setShowImportModal(true);
            loadData(keyword, currentPage);
        } catch (err: any) {
            setImportResult({ updated: 0, skipped: 0, errors: [err.response?.data?.message ?? 'Import failed'] });
            setShowImportModal(true);
        } finally {
            setImporting(false);
        }
    };

    // Pulls employees from Microsoft Graph into the Employee table, then reloads the list
    const handleFetchData = async () => {
        setSyncing(true);
        try {
            const res = await axios.post<SyncResult>(
                `${process.env.REACT_APP_API_END_POINT}/EmployeeSync/RunSync`,
                null,
                { headers: authHeader() }
            );
            setSyncResult(res.data);
            loadData(keyword, currentPage);
        } catch (err: any) {
            setSyncResult({ added: 0, updated: 0, total: 0, error: err.response?.data?.message ?? 'Fetch data failed' });
        } finally {
            setSyncing(false);
        }
    };

    const handleDownloadTemplate = async () => {
        const res = await axios.get(
            `${process.env.REACT_APP_API_END_POINT}/v1/EmployeeCard/template`,
            { headers: authHeader(), responseType: 'blob' }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = 'CardNo_Template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (!token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                if (!res) return;
                setToken(res.accessToken);
                try {
                    const r1 = await axios('https://graph.microsoft.com/v1.0/me/photo/$value', {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                        responseType: 'arraybuffer',
                    });
                    setPhotoData('data:image/jpeg;base64, ' + Buffer.from(r1.data, 'binary').toString('base64'));
                } catch (_) {}
                const userRole = await checkRole(res.accessTokenProfile);
                saveRealRole(userRole);
                setRole(getEffectiveRole(userRole));
                if (userRole !== 'superadmin') {
                    window.location.href = `${window.location.origin}/accessdenied`;
                    return;
                }
                setStateLoad(true);
                loadData('', 0);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inProgress]);

    if (!stateLoad) return <LoadingScreen />;

    const hasErrors = (importResult?.errors?.length ?? 0) > 0;
    const showRange = data.total > 0
        ? `${currentPage * PAGE_SIZE + 1}–${Math.min((currentPage + 1) * PAGE_SIZE, data.total)}`
        : '0';

    return (
        <div className="admin-layout">
            <Topbar token={token} photoData={photoData} role={role} />
            <main className="admin-main">
                <div className="dt-container">

                    {/* Header */}
                    <div className="dt-header">
                        <div>
                            <div className="dt-title">Update Card No</div>
                            <div className="dt-subtitle">จัดการ Card No ของพนักงานทั้งหมด</div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleFetchData}
                                disabled={syncing}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                            >
                                {syncing
                                    ? <><span className="spinner-border spinner-border-sm" /> Fetching…</>
                                    : <><BiRefresh size={16} /> Fetch Data</>
                                }
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleDownloadTemplate}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                            >
                                <PiMicrosoftExcelLogoDuotone size={16} /> Template
                            </button>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={handleExport}
                                disabled={exporting}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                            >
                                {exporting
                                    ? <><span className="spinner-border spinner-border-sm" /> Exporting…</>
                                    : <><BiDownload size={16} /> Export</>
                                }
                            </button>
                            <button
                                className="btn btn-add btn-sm"
                                onClick={() => importRef.current?.click()}
                                disabled={importing}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                                {importing
                                    ? <><span className="spinner-border spinner-border-sm" /> Importing…</>
                                    : <><BiUpload size={16} /> Import</>
                                }
                            </button>
                            <input
                                ref={importRef}
                                type="file"
                                accept=".xlsx,.xls"
                                style={{ display: 'none' }}
                                onChange={handleImportFile}
                            />
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div className="dt-toolbar">
                        <div className="dt-search">
                            <BiSearchAlt className="dt-search-icon" />
                            <input
                                className="dt-search-input"
                                placeholder="Search by Employee ID or Card No…"
                                value={keyword}
                                onChange={e => setKeyword(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                            />
                            {keyword && (
                                <button className="dt-search-clear" onClick={handleClear}>×</button>
                            )}
                        </div>
                        <button
                            className="btn btn-add btn-sm"
                            onClick={handleSearch}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                            <BiSearchAlt size={16} /> Search
                        </button>
                    </div>

                    {/* Table */}
                    <div className="dt-table-wrap">
                        <table className="dt-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 130 }}>Employee ID</th>
                                    <th style={{ width: 150 }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                            <BiIdCard size={15} /> Card No
                                        </span>
                                    </th>
                                    <th>Display Name</th>
                                    <th style={{ width: 180 }}>Department</th>
                                    <th style={{ width: 200 }}>Job Title</th>
                                    <th style={{ width: 230 }}>Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="dt-empty">
                                            <span className="spinner-border spinner-border-sm me-2" />Loading…
                                        </td>
                                    </tr>
                                ) : data.items.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="dt-empty">
                                            <div>No employees found</div>
                                            <small>Try a different search keyword</small>
                                        </td>
                                    </tr>
                                ) : data.items.map((row, i) => (
                                    <tr key={i}>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.employeeId}</td>
                                        <td>
                                            {row.cardNo
                                                ? (
                                                    <span style={{
                                                        background: 'rgba(197,40,31,0.08)',
                                                        color: '#c5281f',
                                                        borderRadius: 6,
                                                        padding: '2px 10px',
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 600,
                                                        border: '1px solid rgba(197,40,31,0.2)',
                                                    }}>{row.cardNo}</span>
                                                )
                                                : <span style={{ color: '#aaa', fontSize: '0.8rem' }}>—</span>
                                            }
                                        </td>
                                        <td>{row.displayName ?? '—'}</td>
                                        <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>{row.department ?? '—'}</td>
                                        <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>{row.jobTitle ?? '—'}</td>
                                        <td style={{ fontSize: '0.85rem' }}>{row.mail ?? '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div className="dt-footer">
                        <div className="dt-count">
                            Showing <strong>{showRange}</strong> of <strong>{data.total}</strong> employees
                        </div>
                        {data.totalPages > 1 && (
                            <ReactPaginate
                                previousLabel="‹"
                                nextLabel="›"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                breakLabel="…"
                                breakClassName="page-item"
                                breakLinkClassName="page-link"
                                pageCount={data.totalPages}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={4}
                                onPageChange={e => handlePageChange(e.selected)}
                                containerClassName="pagination mb-0"
                                activeClassName="active"
                                forcePage={currentPage}
                            />
                        )}
                    </div>
                </div>
            </main>

            {/* Import Result Modal */}
            <Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered>
                <Modal.Header
                    closeButton
                    style={{ borderBottom: `3px solid ${hasErrors ? '#c5281f' : '#1f9d57'}` }}
                >
                    <Modal.Title style={{ color: hasErrors ? '#c5281f' : '#1f9d57', fontSize: '1rem', fontWeight: 700 }}>
                        {hasErrors ? 'Import completed with errors' : 'Import successful'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: 'flex', gap: 32, marginBottom: hasErrors ? 16 : 0 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#1f9d57' }}>
                                {importResult?.updated ?? 0}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Updated</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '2.2rem', fontWeight: 700, color: '#9ca3af' }}>
                                {importResult?.skipped ?? 0}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Skipped</div>
                        </div>
                    </div>
                    {hasErrors && (
                        <div style={{
                            background: 'rgba(197,40,31,0.06)',
                            border: '1px solid rgba(197,40,31,0.2)',
                            borderRadius: 8,
                            padding: '10px 14px',
                            maxHeight: 200,
                            overflowY: 'auto',
                        }}>
                            {importResult!.errors.map((e, i) => (
                                <div key={i} style={{ fontSize: '0.8rem', color: '#c5281f', marginBottom: 4 }}>• {e}</div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImportModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Fetch Data Result Modal */}
            <Modal show={syncResult !== null} onHide={() => setSyncResult(null)} centered>
                <Modal.Header
                    closeButton
                    style={{ borderBottom: `3px solid ${syncResult?.error ? '#c5281f' : '#1f9d57'}` }}
                >
                    <Modal.Title style={{ color: syncResult?.error ? '#c5281f' : '#1f9d57', fontSize: '1rem', fontWeight: 700 }}>
                        {syncResult?.error ? 'Fetch data failed' : 'Fetch data successful'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {syncResult?.error ? (
                        <div style={{ fontSize: '0.85rem', color: '#c5281f' }}>{syncResult.error}</div>
                    ) : (
                        <div style={{ display: 'flex', gap: 32 }}>
                            {[
                                { label: 'Added', value: syncResult?.added, color: '#1f9d57' },
                                { label: 'Updated', value: syncResult?.updated, color: '#2563eb' },
                                { label: 'Total', value: syncResult?.total, color: '#6b7280' },
                            ].map(s => (
                                <div key={s.label} style={{ textAlign: 'center' }}>
                                    <div style={{ fontSize: '2.2rem', fontWeight: 700, color: s.color }}>{s.value ?? 0}</div>
                                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSyncResult(null)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default UpdateCardNo;
